// ── Mock member profile ──────────────────────────────────────────
// Will be replaced by Supabase auth.getUser() + profiles table query
export interface MemberProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  plan: 'Starter' | 'Pro' | 'Elite';
  memberSince: string;
  avatarInitials: string;
  statesMonitored: string[];
  savedCount: number;
  activeDeals: number;
  nextAuction: string;
}

export const mockMember: MemberProfile = {
  id: 'member-001',
  firstName: 'Michael',
  lastName: 'Chen',
  email: 'michael.chen@example.com',
  phone: '(408) 555-0192',
  plan: 'Pro',
  memberSince: 'June 2023',
  avatarInitials: 'MC',
  statesMonitored: ['CA', 'NV', 'AZ'],
  savedCount: 24,
  activeDeals: 7,
  nextAuction: 'Apr 4, 2026',
};

// ── Recent activity feed ─────────────────────────────────────────
export interface ActivityItem {
  id: string;
  type: 'saved' | 'alert' | 'download' | 'viewed' | 'account' | 'bid';
  title: string;
  detail: string;
  time: string;
  state?: 'CA' | 'NV' | 'AZ';
}

export const recentActivity: ActivityItem[] = [
  {
    id: 'a1',
    type: 'saved',
    title: 'Property Saved',
    detail: '2847 Crenshaw Blvd, Los Angeles, CA — $847K market value',
    time: '2 hours ago',
    state: 'CA',
  },
  {
    id: 'a2',
    type: 'alert',
    title: 'New Foreclosure Alert',
    detail: '3 new tax-delinquent properties added in Phoenix, AZ',
    time: '5 hours ago',
    state: 'AZ',
  },
  {
    id: 'a3',
    type: 'download',
    title: 'Guide Downloaded',
    detail: 'The Complete Guide to Tax-Delinquent Property Investing',
    time: 'Yesterday',
  },
  {
    id: 'a4',
    type: 'viewed',
    title: 'Property Viewed',
    detail: '4412 Ocean View Dr, San Diego, CA — $1.15M market value',
    time: 'Yesterday',
    state: 'CA',
  },
  {
    id: 'a5',
    type: 'account',
    title: 'Alert Preferences Updated',
    detail: 'Daily digest enabled for CA + AZ counties',
    time: '2 days ago',
  },
];

// ── Saved / tracked deals ─────────────────────────────────────────
export interface DealItem {
  id: string;
  address: string;
  city: string;
  state: 'CA' | 'NV' | 'AZ';
  marketValue: number;
  currentBid: number;
  equityPct: number;
  auctionDays: number;
  status: 'Watching' | 'Bidding' | 'Won' | 'Passed';
  propertyType: string;
  imageUrl: string;
}

export const savedDeals: DealItem[] = [
  {
    id: 'd1',
    address: '2847 Crenshaw Blvd',
    city: 'Los Angeles',
    state: 'CA',
    marketValue: 847000,
    currentBid: 340000,
    equityPct: 59.9,
    auctionDays: 12,
    status: 'Bidding',
    propertyType: 'Single Family',
    imageUrl: 'https://public.readdy.ai/ai/img_res/a1afefe43f9cd58b79a0eb4f9a497776.jpg',
  },
  {
    id: 'd2',
    address: '4412 Ocean View Dr',
    city: 'San Diego',
    state: 'CA',
    marketValue: 1150000,
    currentBid: 490000,
    equityPct: 57.4,
    auctionDays: 21,
    status: 'Watching',
    propertyType: 'Single Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=coastal%20house%20San%20Diego%20California%20white%20Mediterranean%20architecture%20ocean%20views%20palm%20trees%20real%20estate&width=300&height=200&seq=deal002&orientation=landscape',
  },
  {
    id: 'd3',
    address: '9844 Camelback Rd',
    city: 'Phoenix',
    state: 'AZ',
    marketValue: 620000,
    currentBid: 242000,
    equityPct: 61.0,
    auctionDays: 10,
    status: 'Watching',
    propertyType: 'Single Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20ranch%20house%20Phoenix%20Arizona%20desert%20pool%20terracotta%20tiles%20sunny%20exterior%20luxury%20residential&width=300&height=200&seq=deal003&orientation=landscape',
  },
  {
    id: 'd4',
    address: '789 Desert Rose Ct',
    city: 'Las Vegas',
    state: 'NV',
    marketValue: 385000,
    currentBid: 142000,
    equityPct: 63.1,
    auctionDays: 6,
    status: 'Bidding',
    propertyType: 'Single Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20house%20Las%20Vegas%20Nevada%20desert%20landscaping%20tan%20exterior%20tile%20roof%20mountain%20views&width=300&height=200&seq=deal004&orientation=landscape',
  },
  {
    id: 'd5',
    address: '1177 Via Del Tesoro',
    city: 'Scottsdale',
    state: 'AZ',
    marketValue: 1350000,
    currentBid: 520000,
    equityPct: 61.5,
    auctionDays: 30,
    status: 'Watching',
    propertyType: 'Single Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=luxury%20mansion%20Scottsdale%20Arizona%20desert%20modern%20architecture%20pool%20patio%20terracotta%20exterior%20mountain%20views%20gated%20community&width=300&height=200&seq=deal005&orientation=landscape',
  },
  {
    id: 'd6',
    address: '3301 Lone Mountain Rd',
    city: 'Henderson',
    state: 'NV',
    marketValue: 695000,
    currentBid: 278000,
    equityPct: 60.0,
    auctionDays: 15,
    status: 'Passed',
    propertyType: 'Multi-Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=duplex%20multi-family%20home%20Henderson%20Nevada%20beige%20stucco%20desert%20southwest%20architecture%20residential%20investment&width=300&height=200&seq=deal006&orientation=landscape',
  },
  {
    id: 'd7',
    address: '1523 Sunrise Ave',
    city: 'Sacramento',
    state: 'CA',
    marketValue: 520000,
    currentBid: 215000,
    equityPct: 58.7,
    auctionDays: 8,
    status: 'Won',
    propertyType: 'Single Family',
    imageUrl: 'https://readdy.ai/api/search-image?query=suburban%20house%20Sacramento%20California%20ranch%20style%20green%20lawn%20residential%20real%20estate%20white%20exterior&width=300&height=200&seq=deal007&orientation=landscape',
  },
];
